/**
 * PersonCard — single-user tile used across the Networking surface.
 *
 * Card variant convention (SSOT for when to use row vs stacked):
 *   - `variant="row"` when the card is a **list item** in a vertical feed
 *     (requests received/sent, conversation list).
 *   - `variant="stacked"` when the card is a **gallery tile** in a grid
 *     (discover, connections). Stacked cards have taller media/header slots
 *     and read as a collection; row cards read as a queue.
 *
 * Error / success toasts for connect / accept / decline / cancel actions
 * live in the parent (NetworkingView) because that's where the async
 * calls are dispatched — keeping PersonCard purely presentational.
 */

import { MapPin, MessageSquare, UserPlus, Check, X, UserX, Clock } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Avatar from '../ui/Avatar';
import type { Professional } from '../NetworkingView';
import './PersonCard.css';

export type PersonCardVariant =
    | 'connection'
    | 'discover'
    | 'request-received'
    | 'request-sent';

export type PersonCardProps = {
    professional: Professional;
    variant: PersonCardVariant;
    onMessage?: (p: Professional) => void;
    onConnect?: (id: string) => void;
    onAccept?: (id: string) => void;
    onDecline?: (id: string) => void;
    onCancel?: (id: string) => void;
};

function PersonCard({
    professional,
    variant,
    onMessage,
    onConnect,
    onAccept,
    onDecline,
    onCancel,
}: PersonCardProps) {
    const isRequest = variant === 'request-received' || variant === 'request-sent';

    return (
        <Card
            variant={isRequest ? 'row' : 'stacked'}
            className={`person-card person-card--${variant}`}
        >
            <Card.Header>
                <Avatar
                    name={professional.name}
                    online={professional.online}
                    size="md"
                />
                <div className="person-card__identity">
                    <h3 className="person-card__name">{professional.name}</h3>
                    <p className="person-card__role">{professional.role}</p>
                    <p className="person-card__company">{professional.company}</p>
                </div>
            </Card.Header>

            {!isRequest && (
                <Card.Body>
                    {variant === 'connection' && professional.email && (
                        <p className="person-card__contact">
                            <MapPin size={14} />
                            {professional.location}
                        </p>
                    )}
                    {variant === 'discover' && (
                        <p className="person-card__contact">
                            <MapPin size={14} />
                            {professional.location}
                        </p>
                    )}
                    {professional.skills.length > 0 && (
                        <div className="person-card__skills">
                            {professional.skills.map((skill, i) => (
                                <span key={i} className="ui-chip">
                                    {skill}
                                </span>
                            ))}
                        </div>
                    )}
                </Card.Body>
            )}

            {isRequest && (
                <Card.Body>
                    {variant === 'request-received' && (
                        <p className="person-card__contact">
                            <MapPin size={14} />
                            {professional.location}
                        </p>
                    )}
                    {variant === 'request-sent' && (
                        <p className="person-card__contact">
                            <Clock size={14} />
                            Pending acceptance
                        </p>
                    )}
                </Card.Body>
            )}

            <Card.Footer>
                {variant === 'connection' && (
                    <Button
                        variant="primary"
                        size="md"
                        fullWidth
                        icon={<MessageSquare size={16} />}
                        onClick={() => onMessage?.(professional)}
                    >
                        Message
                    </Button>
                )}
                {variant === 'discover' && (
                    <Button
                        variant="primary"
                        size="md"
                        fullWidth
                        icon={<UserPlus size={16} />}
                        onClick={() => onConnect?.(professional.id)}
                    >
                        Connect
                    </Button>
                )}
                {variant === 'request-received' && (
                    <>
                        <Button
                            variant="primary"
                            size="md"
                            icon={<Check size={18} />}
                            onClick={() => onAccept?.(professional.id)}
                            aria-label={`Accept connection from ${professional.name}`}
                        >
                            Accept
                        </Button>
                        <Button
                            variant="ghost"
                            size="md"
                            icon={<X size={18} />}
                            onClick={() => onDecline?.(professional.id)}
                            aria-label={`Decline connection from ${professional.name}`}
                        >
                            Decline
                        </Button>
                    </>
                )}
                {variant === 'request-sent' && (
                    <Button
                        variant="ghost"
                        size="md"
                        icon={<UserX size={16} />}
                        onClick={() => onCancel?.(professional.id)}
                    >
                        Cancel request
                    </Button>
                )}
            </Card.Footer>
        </Card>
    );
}

export default PersonCard;
